import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot, Mail, ArrowRight, LogOut, Package, Warehouse, Receipt, Info, Command, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const ICONS = { zap91: Package, ewms: Warehouse, bill_booking: Receipt };

export default function ProgramPicker() {
  const { user, logout, setActiveProgram } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState(null);
  const [details, setDetails] = useState(null);

  const isManager = user?.is_admin || user?.role_category === "rc_approver";

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => setPrograms([]));
  }, []);

  useEffect(() => {
    if (programs && programs.length === 1) {
      setActiveProgram(programs[0].id);
      navigate("/app/dashboard", { replace: true });
    }
  }, [programs, setActiveProgram, navigate]);

  const pick = (id) => { setActiveProgram(id); navigate("/app/dashboard"); };

  if (programs === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground kp-paper">Loading…</div>;

  return (
    <div className="min-h-screen kp-paper">
      <header className="h-16 bg-background/80 backdrop-blur-md border-b border-border flex items-center px-6 gap-3 sticky top-0 z-30">
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
          <Bot className="w-5 h-5 text-primary-foreground" />
        </div>
        <span className="font-display text-lg text-foreground">Ask <span className="italic text-primary">Z-Method</span></span>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
          <Button variant="outline" size="sm" onClick={logout} data-testid="picker-logout" className="rounded-full">
            <LogOut className="w-4 h-4 mr-1.5" /> Log out
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-12 kp-up">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-primary flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Hi {user?.name}, welcome back
          </p>
          <h1 className="font-display text-5xl md:text-[64px] leading-[1] text-foreground">
            Choose your <span className="italic text-primary">program</span>.
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl text-[15px] leading-relaxed">
            Program workspaces hold the day-to-day content. Cross-program work — approvals,
            content management, analytics — lives in the Management Console.
          </p>
        </div>

        {isManager && (
          <button onClick={() => navigate("/manage/overview")} data-testid="picker-management-console"
            className="w-full mb-10 text-left rounded-2xl bg-primary p-7 flex items-center gap-5 hover:bg-primary/95 transition-all group relative overflow-hidden kp-up"
            style={{ animationDelay: "80ms" }}>
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-accent/20 blur-3xl" />
            <div className="w-14 h-14 rounded-xl bg-primary-foreground/10 backdrop-blur flex items-center justify-center shrink-0 border border-primary-foreground/15 relative">
              <Command className="w-7 h-7 text-primary-foreground" />
            </div>
            <div className="min-w-0 relative">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-secondary/90">Not tied to a program</p>
              <p className="font-display text-2xl text-primary-foreground mt-0.5">Management Console</p>
              <p className="text-sm text-primary-foreground/70 mt-1">
                Content, approvals, analytics, announcements and administration across {user?.is_admin ? "all programs" : "your programs"}.
              </p>
            </div>
            <ArrowRight className="ml-auto w-6 h-6 text-secondary shrink-0 group-hover:translate-x-1 transition-transform relative" />
          </button>
        )}

        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-4">
          {programs.length} program{programs.length === 1 ? "" : "s"} in your scope
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="program-grid">
          {programs.map((p) => {
            const Icon = ICONS[p.id] || Package;
            return (
              <div key={p.id}
                className="group kp-card kp-card-lift p-6 cursor-pointer flex flex-col relative overflow-hidden"
                onClick={() => pick(p.id)}
                data-testid={`program-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-xl bg-secondary/60 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                    <Icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <span className="text-[11px] font-mono text-muted-foreground bg-muted rounded-md px-2 py-0.5">{p.jira_tag}</span>
                </div>
                <h3 className="font-display text-2xl text-foreground mt-5 leading-tight">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 flex-1 line-clamp-2 leading-relaxed">{p.description}</p>
                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                  <a href={`mailto:${p.pm_email}`} onClick={(e) => e.stopPropagation()}
                    className="text-xs text-muted-foreground flex items-center gap-1.5 hover:text-primary transition kp-link">
                    <Mail className="w-3.5 h-3.5" /> {p.pm_name}
                  </a>
                  <button onClick={(e) => { e.stopPropagation(); setDetails(p); }}
                    className="text-muted-foreground/60 hover:text-primary transition" data-testid={`program-info-${p.id}`}>
                    <Info className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 text-sm font-semibold text-primary flex items-center gap-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                  Enter workspace <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!details} onOpenChange={() => setDetails(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{details?.name}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{details?.code}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-foreground/80 leading-relaxed">{details?.description}</p>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Objectives</p>
              <p className="text-foreground/80">{details?.objectives}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 text-sm">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Program Manager</p>
                <p className="text-foreground font-medium">{details?.pm_name}</p>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Department</p>
                <p className="text-foreground font-medium">{details?.department}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
